/**
 * This is shared logic and UI components used by both BankBeneficiariesModal and WalletBeneficiariesModal.
 */

import { createPortal } from "react-dom";
import { Search, Trash2 } from "lucide-react";

const AVATAR_COLORS = [
  { bg: "#E8F5E9", color: "#2E7D32" },
  { bg: "#E3F2FD", color: "#1565C0" },
  { bg: "#F3E5F5", color: "#6A1B9A" },
  { bg: "#FFF3E0", color: "#E65100" },
  { bg: "#FCE4EC", color: "#880E4F" },
  { bg: "#E0F2F1", color: "#00695C" },
];

const getInitials = (name = "") =>
  name
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join("");

const getAvatarColor = (id) => AVATAR_COLORS[id % AVATAR_COLORS.length];

export function BeneficiaryList({
  beneficiaries,
  searchQuery,
  onSearchChange,
  onClose,
  onSelect,
  onDelete,
  getSubLabel,
  emptyMessage = "You have not saved any beneficiary yet.",
}) {
  const searched = beneficiaries.filter((b) =>
    b.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <>
      <div
        className="modal-header border-0 flex-column align-items-stretch p-4"
        style={{ background: "#0F5C30" }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2
            className="fw-semibold mb-0"
            style={{ fontSize: "0.95rem", color: "#fff" }}
          >
            Select Beneficiary
          </h2>
          <button
            type="button"
            className="btn-close btn-close-white"
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        <div
          className="d-flex align-items-center gap-2 px-3"
          style={{
            height: 40,
            background: "rgba(255,255,255,0.12)",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 8,
          }}
        >
          <Search
            size={14}
            color="rgba(255,255,255,0.55)"
            style={{ flexShrink: 0 }}
          />
          <div
            className="d-flex align-items-center flex-grow-1 position-relative"
            style={{ height: 40 }}
          >
            <input
              type="text"
              className="border-0 shadow-none w-100"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              style={{
                background: "transparent",
                outline: "none",
                fontSize: "0.82rem",
                color: "#fff",
                padding: 0,
                height: "auto",
              }}
            />
            {!searchQuery && (
              <span
                className="position-absolute"
                style={{
                  left: 0,
                  fontSize: "0.82rem",
                  lineHeight: 1,
                  color: "rgba(255,255,255,0.45)",
                  pointerEvents: "none",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Search by name...
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Only this section scrolls; overscrollBehavior stops the scroll
          from carrying over to the page at the end of the list. */}
      <div
        className="modal-body px-4 pb-3 pt-2"
        style={{ overscrollBehavior: "contain" }}
      >
        {beneficiaries.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted small mb-0">{emptyMessage}</p>
          </div>
        ) : (
          <div className="d-flex flex-column">
            {searched.length === 0 ? (
              <p className="text-muted small text-center py-3 mb-0">
                No beneficiaries match your search.
              </p>
            ) : (
              searched.map((beneficiary) => {
                const { bg, color } = getAvatarColor(beneficiary.id);
                return (
                  <div
                    key={beneficiary.id}
                    className="d-flex align-items-center gap-3 px-2 py-2 rounded-3"
                    style={{ cursor: "pointer" }}
                    onClick={() => onSelect(beneficiary)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-semibold"
                      style={{
                        width: 40,
                        height: 40,
                        fontSize: 12,
                        background: bg,
                        color,
                      }}
                    >
                      {getInitials(beneficiary.name)}
                    </div>

                    <div className="flex-grow-1 min-w-0">
                      <p
                        className="fw-semibold mb-0 text-truncate"
                        style={{ fontSize: "0.88rem", color: "#0d1b12" }}
                      >
                        {beneficiary.name}
                      </p>
                      <p
                        className="text-muted mb-0"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {getSubLabel(beneficiary)}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 30,
                        height: 30,
                        padding: 0,
                        border: "0.5px solid #dee2e6",
                        borderRadius: 8,
                        background: "transparent",
                        color: "#adb5bd",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(beneficiary.id);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#FEE2E2";
                        e.currentTarget.style.borderColor = "#FCA5A5";
                        e.currentTarget.style.color = "#DC2626";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = "#dee2e6";
                        e.currentTarget.style.color = "#adb5bd";
                      }}
                      aria-label="Delete beneficiary"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </>
  );
}

export function ConfirmDeleteModal({
  confirmDeleteId,
  beneficiaries,
  onCancel,
  onConfirm,
}) {
  if (!confirmDeleteId) return null;

  return createPortal(
    <div
      className="modal fade show"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{
        display: "block",
        zIndex: 1060,
        background: "rgba(0, 0, 0, 0.65)", // Dims the beneficiary modal behind it.
      }}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        role="document"
        style={{ maxWidth: 380 }} // Narrower than the beneficiary modal so it reads as a separate layer.
      >
        <div
          className="modal-content overflow-hidden border-0"
          style={{ boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)" }}
        >
          <div className="modal-header bg-danger border-0 flex-column align-items-center justify-content-center p-4">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{
                width: 48,
                height: 48,
                background: "rgba(255,255,255,0.15)",
              }}
            >
              <Trash2 size={22} color="#fff" />
            </div>
            <h3
              className="fw-semibold text-white mb-0"
              style={{ fontSize: "0.95rem" }}
            >
              Delete beneficiary
            </h3>
          </div>

          <div className="modal-body text-center px-4 py-4">
            {beneficiaries
              .filter((b) => b.id === confirmDeleteId)
              .map((b) => (
                <p
                  key={b.id}
                  className="text-secondary small mb-0"
                  style={{ lineHeight: 1.6 }}
                >
                  Are you sure you want to remove{" "}
                  <strong className="text-dark">{b.name}</strong> as a
                  beneficiary?
                </p>
              ))}
          </div>

          <div className="modal-footer border-0 px-4 pb-4 pt-0 gap-2">
            <button
              type="button"
              className="btn btn-sm btn-light flex-fill"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger flex-fill"
              onClick={onConfirm}
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}